/**
 * MainSummaryTab — the /main "Summary" readout.
 *
 * A one-screen exec readout that pairs human-authored narrative
 * (main-readout-content.ts, reviewed each cycle) with live Salesforce figures
 * injected via {{token}} placeholders (resolved by main-readout.ts). Answers the
 * three product questions in order and links each section to its detail tab.
 *
 * Reads the same /api/main/scorecard payload as the tile tabs (useMainScorecard),
 * so numbers here match the tiles and stay current to the last cache refresh.
 */
import {
  READOUT_INTRO,
  READOUT_REVIEWED_ON,
  READOUT_SECTIONS,
} from '../../lib/main-readout-content.js';
import { resolveText, resolveToken } from '../../lib/main-readout.js';
import { useExecPanels } from '../../queries/useExecPanels.js';
import { useMainScorecard } from '../../queries/useMainScorecard.js';
import { Card, CardContent } from '../ui/Card.js';

import type { TabKey } from './MainTabBar.js';

interface MainSummaryTabProps {
  /** Navigate to a detail tab when the reader clicks "See the detail". */
  onNavigateTab: (tab: TabKey) => void;
}

/** Format the cache-refresh instant as a friendly local date-time. */
function formatRefreshed(iso: string | undefined): string {
  if (!iso) return 'unknown';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.valueOf())) return 'unknown';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
}

export function MainSummaryTab({ onNavigateTab }: MainSummaryTabProps): React.JSX.Element {
  const { data: scorecard, isLoading } = useMainScorecard();
  const { data: exec } = useExecPanels();
  const refreshedAt = scorecard?.freshness.overall_last_refreshed_at;

  return (
    <div className="space-y-6">
      {/* Intro / how-to-read banner */}
      <Card className="border-ink-100 bg-ink-50/40">
        <CardContent className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h1 className="text-lg font-semibold text-ink-900">Executive readout</h1>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-good-bg px-2.5 py-0.5 text-xs font-semibold text-good-text">
              <span aria-hidden>●</span> Live from Salesforce · refreshed {formatRefreshed(refreshedAt)}
            </span>
          </div>
          <p className="max-w-3xl text-sm leading-relaxed text-ink-600">{READOUT_INTRO}</p>
          <p className="text-xs text-ink-400">Narrative last reviewed {READOUT_REVIEWED_ON}</p>
        </CardContent>
      </Card>

      {isLoading && (
        <p className="text-sm italic text-ink-400" data-testid="summary-loading">
          Loading live figures…
        </p>
      )}

      {/* One card per exec question */}
      {READOUT_SECTIONS.map((section) => (
        <Card key={section.id} data-testid={`summary-section-${section.id}`}>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <h2 className="text-base font-semibold text-ink-900">{section.question}</h2>
              <p className="text-sm text-ink-500">{section.standfirst}</p>
            </div>

            {/* Key-number strip */}
            <div className="flex flex-wrap gap-2">
              {section.highlights.map((h) => (
                <span
                  key={h.token}
                  className="inline-flex items-baseline gap-1.5 rounded-lg border border-ink-100 bg-white px-2.5 py-1"
                >
                  <span className="text-[11px] uppercase tracking-wide text-ink-400">{h.label}</span>
                  <span className="text-sm font-semibold tabular-nums text-ink-900">
                    {resolveToken(scorecard, h.token, exec)}
                  </span>
                </span>
              ))}
            </div>

            {/* Narrative paragraphs with live numbers spliced in */}
            <div className="max-w-3xl space-y-2.5">
              {section.body.map((para, i) => (
                <p key={i} className="text-sm leading-relaxed text-ink-700">
                  {resolveText(scorecard, para, exec)}
                </p>
              ))}
            </div>

            {/* Strategic read — one durable executive-level takeaway per section. */}
            <div className="max-w-3xl border-l-2 border-brand-500 pl-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-600">
                Strategic read
              </p>
              <p className="mt-0.5 text-sm font-medium leading-relaxed text-ink-800">
                {section.strategic}
              </p>
            </div>

            <button
              type="button"
              onClick={() => { onNavigateTab(section.tab); }}
              className="text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              See the detail in {section.tabLabel} →
            </button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
